"use client";

/**
 * UserContext — the signed-in identity, loaded from the Central Server via
 * GET /api/me (which proxies to /auth/me and transparently refreshes an
 * expired access token). READ-ONLY: nothing in the client mutates identity.
 *
 * The compact SessionUser from the Server is mapped to the richer ServerUser
 * the UI renders; fields the Server does not yet expose stay empty.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { usePathname } from "next/navigation";
import type { ServerUser, SessionUser } from "@/types/server";
import { mapSessionToServerUser } from "@/lib/session";
import { fetchMe } from "@/features/auth/api/authClient";

type IdentityStatus = "loading" | "unprovisioned" | "ready";

interface UserContextValue {
  user: ServerUser | null;
  session: SessionUser | null;
  status: IdentityStatus;
  refresh: () => Promise<void>;
}

const UserContext = createContext<UserContextValue | null>(null);

function isAuthRoute(pathname: string): boolean {
  return pathname.startsWith("/login") || pathname.startsWith("/auth");
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [session, setSession] = useState<SessionUser | null>(null);
  const [status, setStatus] = useState<IdentityStatus>("loading");

  const refresh = useCallback(async () => {
    if (isAuthRoute(pathname)) {
      setSession(null);
      setStatus("unprovisioned");
      return;
    }
    setStatus("loading");
    const me = await fetchMe();
    setSession(me);
    setStatus(me ? "ready" : "unprovisioned");
  }, [pathname]);

  useEffect(() => {
    // Loads identity on mount / route change; the sync prefix of refresh()
    // (loading + auth-route reset) is intentional and hydration-safe.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
  }, [refresh]);

  const value = useMemo<UserContextValue>(
    () => ({
      user: session ? mapSessionToServerUser(session) : null,
      session,
      status,
      refresh,
    }),
    [session, status, refresh],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser(): UserContextValue {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within UserProvider");
  }
  return context;
}
